import { Parser } from "html-to-react";
import React, { useMemo } from "react";

import { createProcessingInstructions } from "./processors";
import ProcessAdditionalContext from "./processors/additionalContext";
import ProcessLinks from "./processors/links";
import ProcessMarks from "./processors/marks";
import ProcessMath from "./processors/math";
import {
  RichContentDisplayOptions,
  useRichContentDisplayOptions,
} from "./RichContentDisplayOptions";
import { sanitizeRichContent } from "./utils";

export type RichContentRenderOptions = {
  isSanitized?: boolean;
  hasRouter?: boolean;
};

export const renderRichContentForDisplay = (
  content: string | null | undefined,
  displayOptions?: RichContentDisplayOptions,
  renderOptions?: RichContentRenderOptions,
  language?: string,
): JSX.Element => {
  if (content == null) return <></>;

  content = sanitizeRichContent(
    content,
    displayOptions?.sanitize,
    renderOptions?.isSanitized,
  );

  const htmlToReactParser = new Parser();

  const processors = [
    ProcessMath,
    ProcessMarks(displayOptions?.marks),
    ProcessLinks,
    ProcessAdditionalContext(
      displayOptions?.showAdditionalContext,
      displayOptions?.sanitize,
    ),
  ];

  return (
    <>
      {htmlToReactParser.parseWithInstructions(
        content || "",
        () => true,
        createProcessingInstructions(processors, language),
      )}
    </>
  );
};

export const useRichContentRenderedForDisplay = (
  content: string | null | undefined,
  options?: RichContentDisplayOptions,
  language?: string,
) => {
  const optionsMemo = useRichContentDisplayOptions(options);

  const sanitizedContent = useMemo(
    () => sanitizeRichContent(content, options?.sanitize),
    [content, options?.sanitize],
  );

  return useMemo(() => {
    return renderRichContentForDisplay(
      sanitizedContent,
      optionsMemo,
      {
        isSanitized: true,
      },
      language,
    );
  }, [optionsMemo, sanitizedContent, language]);
};
